import { useCallback } from 'react';

import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';

import { fg } from '@confluence/feature-gating';
import {
	type ExtensionViewportSize,
	useExtensionViewportSizes,
} from '@confluence/forge-ui/entry-points/useExtensionViewportSizes.ts';

const FORGE_VIEWPORT_SIZES = ['small', 'medium', 'default', 'large', 'xlarge'];

const FORGE_MANIFEST_VIEWPORT_HEIGHTS = {
	small: '112px',
	medium: '262px',
	default: '262px',
	large: '524px',
	xlarge: '1048px',
} as const;

/**
 * Currently, very few legacy (connect) macros provide their expected rendered height/width
 * values in their macro parameters. Of those that do, there's a slight but consistent
 * margin of error for the height value. Others that don't render dynamic content we can
 * set a default height for (such as Miro embed).
 *
 * Any changes here should be mirrored in content-renderer-legacy-macros/src/useConnectExtensionViewportSize.ts
 * and getCalculatedHeightFromNode.ts
 */
const DRAW_IO_HEIGHT_OFFSET = 52;
const GOOGLE_DRIVE_EMBED_FILE_OFFSET_HEIGHT = 79;
const PLANTUML_OFFSET_HEIGHT = 40;

// Default sizes
const MIRO_EMBED_DEFAULT_HEIGHT = 570;
const EASY_MATH_BLOCK_DEFAULT_HEIGHT = 47;
const MATH_BLOCK_DEFAULT_HEIGHT = 58;
const PAGE_SIGNATURES_DEFAULT_HEIGHT = 23;
const QUESTIONS_LIST_DEFAULT_HEIGHT = 800;
const JIRA_ROADMAP_DEFAULT_HEIGHT = 600;
const LUCID_CHART_DEFAULT_HEIGHT = 500;
const GLIFFY_DEFAULT_HEIGHT = 150;
const AURA_DIVIDER_DEFAULT_HEIGHT = 21;
const AURA_CARDS_DEFAULT_HEIGHT = 226;

const getForgeExtensionHeight = (
	extensionId?: string,
	extensionViewportSizes?: ExtensionViewportSize[],
): string | undefined => {
	if (!extensionViewportSizes || !extensionId || !Array.isArray(extensionViewportSizes)) return;

	const viewportSize = extensionViewportSizes.find(
		(ext) => ext.extensionId === extensionId,
	)?.viewportSize;

	if (!viewportSize) {
		return undefined;
	}

	if (FORGE_VIEWPORT_SIZES.includes(viewportSize)) {
		return FORGE_MANIFEST_VIEWPORT_HEIGHTS[viewportSize];
	}

	return undefined;
};

const getHeightFromNode = (node: Node) => {
	if (!node) return;

	const extensionKey = node.attrs.extensionKey;
	const params = node.attrs.parameters?.macroParams;

	/**
	 * DrawIo will resize diagrams based on the viewport they are rendered in
	 * if the diagram's width is greater than 530px. This means that the natural dimensions
	 * provided in macro parameters could be the incorrect height to reserve so we will
	 * conservatively exclude them.
	 */
	if (
		extensionKey === 'drawio' ||
		extensionKey === 'inc-drawio' ||
		extensionKey === 'drawio-sketch'
	) {
		const diagramWidth = Number(params?.width?.value);
		const zoomFactor = Number(params?.zoom?.value);

		if ((diagramWidth && diagramWidth > 530) || zoomFactor !== 1) {
			// Exclude wide diagrams due to allow resizing by vendor
			return;
		}
	}

	if (extensionKey === 'plantumlcloud') {
		return params?.originalHeight?.value;
	}

	return params?.height?.value;
};

export const useExtensionNodeParams = () => {
	/**
	 * To attribute impact to each ecosystem, we plan to rollout Forge improvements separately
	 * via (confluence_preload_forge_viewport_heights_editor) with Connect improvements after
	 * via (confluence_connect_macro_preset_height). Thus we'll isolate Forge functionality first with
	 * the long term plan to merge both into a single function after successful rollout.
	 */
	const extensionViewportSizes = useExtensionViewportSizes(
		!fg('confluence_preload_forge_viewport_heights_editor'),
	);

	const getForgeViewportHeight: GetPMNodeHeight = (node: Node) => {
		// Forge in-content macros contain an ARI/environment/appKey extensionId value
		const extensionId = node.attrs.parameters?.extensionId;
		const forgeExtensionHeight = getForgeExtensionHeight(extensionId, extensionViewportSizes);

		if (forgeExtensionHeight) return forgeExtensionHeight;
	};

	const getCalculatedHeightFromNode: GetPMNodeHeight = useCallback((node: Node) => {
		// Check if it's a Connect extension with macro parameters
		const extensionKey = node.attrs.extensionKey;
		const nodeHeight = getHeightFromNode(node);

		let height: number | undefined = Number(nodeHeight);

		if (!Number.isNaN(height)) {
			// for extensions that provide a number, add their offset
			switch (extensionKey) {
				case 'drawio':
				case 'inc-drawio':
				case 'drawio-sketch':
					height += DRAW_IO_HEIGHT_OFFSET;
					break;
				case 'lref-gdrive-embedded-file':
					height += GOOGLE_DRIVE_EMBED_FILE_OFFSET_HEIGHT;
					break;
				case 'plantumlcloud':
					height += PLANTUML_OFFSET_HEIGHT;
					break;
				default:
					break;
			}
		} else {
			// for extensions that don't resize based on their content, we can set a default height
			switch (extensionKey) {
				case 'gliffy':
					height = GLIFFY_DEFAULT_HEIGHT;
					break;
				case 'aura-divider':
					height = AURA_DIVIDER_DEFAULT_HEIGHT;
					break;
				case 'aura-cards':
					height = AURA_CARDS_DEFAULT_HEIGHT;
					break;
				case 'miro-macro-resizing':
					height = MIRO_EMBED_DEFAULT_HEIGHT;
					break;
				case 'easy-math-block':
					height = EASY_MATH_BLOCK_DEFAULT_HEIGHT;
					break;
				case 'mathblock':
					height = MATH_BLOCK_DEFAULT_HEIGHT;
					break;
				case 'page-signatures':
					height = PAGE_SIGNATURES_DEFAULT_HEIGHT;
					break;
				case 'questionslist-macro':
					height = QUESTIONS_LIST_DEFAULT_HEIGHT;
					break;
				case 'jiraroadmap':
					height = JIRA_ROADMAP_DEFAULT_HEIGHT;
					break;
				case 'lucidchart':
					height = LUCID_CHART_DEFAULT_HEIGHT;
					break;
				default:
					height = undefined;
					break;
			}
		}

		return height?.toString();
	}, []);

	if (fg('confluence_preload_forge_viewport_heights_editor')) return getForgeViewportHeight;

	if (fg('confluence_connect_macro_preset_height')) return getCalculatedHeightFromNode;
};
